import {
  createBrowserRouter,
  Navigate,
  RouterContextProvider,
} from "react-router-dom";
import AppLoadingScreen from "@/components/organisms/appLoadingScreen";
import { CONSOLE_SHELL_ROUTE_RELATIVE_PATHS } from "@/constants/consoleShellRoutes";
import { NAV_ITEMS } from "@/constants/navigation";
import { routeAccessMiddleware } from "@/router/routeAccessMiddleware";
import { AppStoreRouterContext } from "@/router/routerAppContext";
import { ROOT_SHELL_ROUTE_ID } from "@/router/rootShellRouteId";
import type { AppStore } from "@/store/app/storeTypes";
import { injectEpics } from "@/store/epics/injectableEpicRuntime";
import { injectSparxsEditorAuxiliaryReducers } from "@/store/app/injectableEditorReducerRuntime";

const sparxsCatalogRoute = () =>
  Promise.all([
    import("@/features/sparxsCatalog/Page"),
    import("@/router/loaders/sparxsCatalogRouteLoader"),
  ]).then(([pageMod, loaderMod]) => ({
    Component: pageMod.default,
    loader: loaderMod.sparxsCatalogRouteLoader,
    shouldRevalidate: loaderMod.sparxsCatalogShouldRevalidate,
  }));

const channelDetailRoute = () =>
  Promise.all([
    import("@/features/channelDetail/Page"),
    import("@/features/channelDetail/channelDetailRouteLoader"),
  ]).then(([pageMod, loaderMod]) => ({
    Component: pageMod.default,
    loader: loaderMod.channelDetailRouteLoader,
    shouldRevalidate: loaderMod.channelDetailShouldRevalidate,
  }));

const executiveSummaryRoute = () =>
  Promise.all([
    import("@/features/executiveSummary/Page"),
    import("@/features/executiveSummary/executiveSummaryRouteLoader"),
  ]).then(([pageMod, loaderMod]) => ({
    Component: pageMod.default,
    loader: loaderMod.executiveSummaryRouteLoader,
    shouldRevalidate: loaderMod.executiveSummaryShouldRevalidate,
  }));

const sparxsEditorRoute = () =>
  Promise.all([
    import("@/router/loaders/sparxsEditorEntryLoader"),
    import("@/features/sparxsEditor/store/epics/sparxsEditorSaveEpic"),
    import("@/features/sparxsEditor/store/epics/sparxsEditorBlueprintLoadEpic"),
    import("@/features/sparxsEditor/store/epics/sparxsEditorTeaserLoadEpic"),
    import("@/features/sparxsEditor/Page"),
    injectSparxsEditorAuxiliaryReducers(),
  ]).then(
    ([
      entryLoaderMod,
      saveEpicMod,
      blueprintLoadEpicMod,
      teaserLoadEpicMod,
      pageMod,
      _,
    ]) => {
      injectEpics(
        saveEpicMod.default,
        blueprintLoadEpicMod.default,
        teaserLoadEpicMod.default,
      );
      return {
        Component: pageMod.default,
        loader: entryLoaderMod.sparxsEditorEntryLoader,
        shouldRevalidate: entryLoaderMod.sparxsEditorShouldRevalidate,
      };
    },
  );

const consoleDefaultIndexRoute = () =>
  import("@/router/loaders/consoleDefaultIndexLoader").then((m) => ({
    loader: m.consoleDefaultIndexLoader,
  }));

const svgAlterationRoute = () =>
  import("@/features/svgAlteration/Page").then((m) => ({
    Component: m.default,
  }));

const pagesShellLayoutRoute = () =>
  Promise.all([
    import("@/router/loaders/rootShellLoader"),
    import("@/router/pagesShellLayout"),
    import("@/router/actions/rootShellAction"),
  ]).then(([loaderMod, layoutMod, actionMod]) => ({
    Component: layoutMod.PagesShellLayout,
    loader: loaderMod.rootShellLoader,
    action: actionMod.rootShellAction,
    shouldRevalidate: actionMod.rootShellShouldRevalidate,
  }));

const consoleShellRoutes = [
  {
    id: ROOT_SHELL_ROUTE_ID,
    path: "/",
    middleware: [routeAccessMiddleware],
    lazy: pagesShellLayoutRoute,
    hydrateFallbackElement: <AppLoadingScreen />,
    children: [
      {
        index: true,
        lazy: consoleDefaultIndexRoute,
      },
      {
        path: CONSOLE_SHELL_ROUTE_RELATIVE_PATHS.EXECUTIVE_SUMMARY,
        lazy: executiveSummaryRoute,
        handle: { title: NAV_ITEMS.EXECUTIVE_SUMMARY.name },
      },
      {
        path: CONSOLE_SHELL_ROUTE_RELATIVE_PATHS.CHANNEL_DETAIL,
        lazy: channelDetailRoute,
        handle: { title: NAV_ITEMS.CHANNEL_DETAIL.name },
      },
      {
        path: CONSOLE_SHELL_ROUTE_RELATIVE_PATHS.SPARX_CATALOG,
        lazy: sparxsCatalogRoute,
        handle: { title: NAV_ITEMS.SPARX_CATALOG.name },
      },
      {
        path: CONSOLE_SHELL_ROUTE_RELATIVE_PATHS.SPARXS_EDITOR,
        lazy: sparxsEditorRoute,
        handle: { title: NAV_ITEMS.SPARXS_EDITOR.name },
      },
      {
        path: CONSOLE_SHELL_ROUTE_RELATIVE_PATHS.SVG_ALTERATION,
        lazy: svgAlterationRoute,
        handle: { title: NAV_ITEMS.SVG_ALTERATION.name },
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
];

/** Invoke after `createAppStore`; passes the same store instance into route middleware via `getContext`. */
export const createAppRouter = (store: AppStore) =>
  createBrowserRouter(consoleShellRoutes, {
    getContext: () => {
      const context = new RouterContextProvider();
      context.set(AppStoreRouterContext, store);
      return context;
    },
  });

export type AppRouter = ReturnType<typeof createAppRouter>;
